import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/views/auth/auth.store'
import { routeNames } from '@/router/route-names-registry'

// Home Page configuration by role
export const HOME_PAGE_BY_ROLE: Record<TUserRoles, TRouteName> = {
  senior: 'seniorComposeRequest',
  volunteer: 'volunteerRequests'
}

// Signup page configuration by role (preselected via `?role=` from the landing page)
const SIGNUP_PAGE_BY_ROLE: Record<TUserRoles, TRouteName> = {
  senior: 'authSignupSenior',
  volunteer: 'authSignupVolunteer'
}

const isUserRole = (role: unknown): role is TUserRoles => typeof role === 'string' && role in SIGNUP_PAGE_BY_ROLE

export const getOnboardingRouteName = (role: unknown): TRouteName => isUserRole(role) ? SIGNUP_PAGE_BY_ROLE[role] : 'authRoleSelection'

// Onboarding page routes (for users who haven't completed registration)
const ONBOARDING_PAGES: TRouteName[] = [
  'authRoleSelection',
  'authSignupSenior',
  'authSignupVolunteer'
]

export const routeGuard = async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()

  const isRootPage = to.name === 'Root'
  const isLoginPage = to.name === routeNames.authLogin
  const isOnboardingPage = ONBOARDING_PAGES.includes(to.name as TRouteName)
  const isErrorPage = to.name === routeNames.error
  const requiresAuth = to.meta.requiresAuth as boolean
  const requiredRole = to.meta.role
  const requiresAdmin = to.meta.requiresAdmin!

  if (isErrorPage) {
    return next()
  }

  // 1. Load session from Supabase
  const session = await authService.getSession()

  // 2. Check if user exists in backend if we have session
  if (session && !authStore.user && !isOnboardingPage) {
    try {
      await authStore.fetchInitialInfo()
    } catch (error) {
      console.error(error)
      return next({ name: routeNames.error })
    }
  }

  // Rule 1: Registered users redirect to their home page when:
  // - Accessing root page
  // - Accessing the login page
  // - Accessing pages for wrong role
  // - Accessing admin pages without admin flag
  if (session &&
    authStore.user &&
    (isRootPage ||
      isLoginPage ||
      (requiredRole && authStore.user?.role !== requiredRole) ||
      (requiresAdmin && !authStore.user?.isAdmin))
  ) {
    return next({ name: HOME_PAGE_BY_ROLE[authStore.user?.role] })
  }

  // Rule 2: Non-authenticated users redirect to login when:
  // - Accessing root page
  // - Accessing protected pages
  // - Accessing onboarding pages
  if (!session && (isRootPage || requiresAuth || isOnboardingPage)) {
    return next({ name: routeNames.authLogin })
  }

  // Rule 3: Authenticated but not registered users redirect to role selection
  // (or straight to the signup page when the role is preselected)
  if (session && !authStore.user && !isOnboardingPage) {
    return next({ name: getOnboardingRouteName(to.query.role) })
  }

  // Allow navigation
  next()
}
